'use client';

import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import clsx from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';

const ClientTopCarousel = props => {
  const { banners } = props;

  const moreThanOne = banners.length > 1;
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const pointerDownHandler = () => {
      if (moreThanOne) {
        carouselApi?.plugins()?.autoplay?.stop();
      }
    };

    const pointerUpHandler = () => {
      if (moreThanOne) {
        carouselApi?.plugins()?.autoplay?.play();
      }
    };

    carouselApi.on('pointerDown', pointerDownHandler);
    carouselApi.on('pointerUp', pointerUpHandler);

    return () => {
      carouselApi.off('pointerDown', pointerDownHandler);
      carouselApi.off('pointerUp', pointerUpHandler);
    };
  }, [carouselApi, moreThanOne]);

  return (
    <div>
      <Carousel
        opts={{
          loop: true,
        }}
        setApi={setCarouselApi}
        plugins={[
          Autoplay({
            playOnInit: true,
            delay: 5000,
          }),
        ]}
        onMouseEnter={() => {
          if (moreThanOne) {
            carouselApi?.plugins()?.autoplay?.stop();
          }
        }}
        onMouseLeave={() => {
          if (moreThanOne) {
            carouselApi?.plugins()?.autoplay?.play();
          }
        }}
      >
        <CarouselContent>
          {banners.map((x, i) => {
            return (
              <CarouselItem key={x.id}>
                <Link
                  href={x.link}
                  className='relative block'
                >
                  <Image
                    className='block h-auto w-full lg_hidden'
                    src={x.m_pic}
                    width={375}
                    height={600}
                    alt={x.title}
                    priority={i === 0}
                    unoptimized
                  />
                  <Image
                    className=' hidden h-auto  w-full lg_block'
                    src={x.p_pic}
                    width={1920}
                    height={800}
                    alt={x.title}
                    priority={i === 0}
                    unoptimized
                  />
                </Link>
              </CarouselItem>
            );
          })}
        </CarouselContent>

        {moreThanOne ? (
          <>
            <div
              className={clsx(
                'absolute left-0 top-1/2 hidden -translate-y-1/2 lg_flex',
                'pointer-events-auto  h-[66px] w-10 cursor-pointer items-center justify-center bg-f8f3ef/60',
              )}
              onClick={() => {
                carouselApi?.scrollPrev();
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='12'
                height='22'
                fill='none'
                viewBox='0 0 12 22'
              >
                <g clipPath='url(#clip0_13425_30108)'>
                  <path
                    stroke='#80583F'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M10.614 1 1.383 11l9.23 10'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_13425_30108'>
                    <path
                      fill='#fff'
                      d='M.383 0h11.23v22H.384z'
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div
              className={clsx(
                'absolute right-0 top-1/2 hidden -translate-y-1/2 lg_flex',
                'pointer-events-auto  h-[66px] w-10 cursor-pointer items-center justify-center bg-f8f3ef/60',
              )}
              onClick={() => {
                carouselApi?.scrollNext();
              }}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='12'
                height='22'
                fill='none'
                viewBox='0 0 12 22'
              >
                <g clipPath='url(#clip0_14494_53937)'>
                  <path
                    stroke='#80583F'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='m1.386 21 9.231-10-9.23-10'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_14494_53937'>
                    <path
                      fill='#fff'
                      d='M11.617 22H.387V0h11.23z'
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </>
        ) : null}
      </Carousel>

      <CarouselDots
        api={carouselApi}
        banners={banners}
      />
    </div>
  );
};

const useDotButton = emblaApi => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onDotButtonClick = useCallback(
    index => {
      if (!emblaApi) {
        return;
      }

      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const onInit = emblaApi => {
      if (!emblaApi) {
        return;
      }

      setScrollSnaps(emblaApi.scrollSnapList());
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    const onSelect = emblaApi => {
      if (!emblaApi) {
        return;
      }

      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('reInit', onInit);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick,
  };
};

const CarouselDots = props => {
  const { api, banners } = props;

  const { selectedIndex, onDotButtonClick } = useDotButton(api);

  if (banners.length <= 1) {
    return null;
  }

  return (
    <div className='mt-[15px] flex items-center justify-center text-[0]  lg_mt-5'>
      {banners.map((_, i) => {
        return (
          <button
            type='button'
            onClick={() => {
              onDotButtonClick(i);
            }}
            onMouseEnter={() => {
              api?.plugins()?.autoplay?.stop();
            }}
            onMouseLeave={() => {
              api?.plugins()?.autoplay?.play();
            }}
            key={i}
            className={clsx(
              'h-6 w-6 ',
              'relative',
              'after_absolute after_left-1/2 after_top-1/2 after_block after_-translate-x-1/2 after_-translate-y-1/2 after_content-[""]',
              'after_h-2 after_w-2 after_rounded-[50%] after_border-1 after_border-solid after_border-BE9983 lg_after_h-3 lg_after_w-3',
              selectedIndex === i ? 'after_bg-BE9983' : 'after_bg-transparent',
            )}
            aria-label={`carousel dot ${i + 1}`}
          />
        );
      })}
    </div>
  );
};

export default ClientTopCarousel;
